import { Injectable } from '@angular/core';
import { NaturalId } from '../map/menu/right-menu/layers-menu/population-menu/population.service';
import { SubscriptionPlans } from './user/user.model';
import { catchError, Observable, of, shareReplay, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { JsonResponse } from '../shared/api/backend-config';
import { environment } from '../../environments/environment';
import { GoogleAnalyticsService } from './google-analytics.service';

export interface AccessibleFeaturesIds {
  h3r7Cells: NaturalId[];
  h3r5Cells: NaturalId[];
}

/** Frontend UI tabs */
export type RegionType = 'COUNTY' | 'STATE' | 'FULL_US';

/** Matches backend SubscriptionController.SubscriptionRegionAccessFrontendDto */
export type BackendRegionType = 'COUNTY' | 'STATE' | 'COUNTRY';

export function toBackendRegionType(type: RegionType): BackendRegionType {
  return type === 'FULL_US' ? 'COUNTRY' : type;
}

export interface SubscriptionRegion {
  id: string;
  displayName: string; // e.g. "Lancaster County, NE" — state abbreviation already included
  regionType: 'COUNTY' | 'STATE' | 'COUNTRY';
}

export enum AccessibleFeatureState {
  LIMITED,
  ALL,
}

// If user payment card is linked and additional actions in stripe are not required for payment, then paymentUrl is null
export type SubscriptionPaymentUrl = {
  paymentUrl: string | null;
};

/** Matches backend CheckoutSessionAmount. */
export interface CheckoutSessionAmount {
  /** Minor units, as Stripe reports it: cents for USD. */
  amountTotal: number;
  /** ISO code, lowercase as Stripe returns it. */
  currency: string;
  paid: boolean;
}

export interface PromoCodeLookup {
  // true if can be applied
  // false if not found or expired or already used
  active: boolean;
  // list of subscriptions for which such promo code can be applied
  toSubscriptions: SubscriptionPlans[];
  // for how many days subscription will be free. Divide to 30 and round to get months
  numberOfFreeDays: number | null;
  amountOff: number | null;
  percentOff: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionsService {
  private readonly apiURL = environment.apiUrl;
  private readonly userSubscriptionURL = this.apiURL + 'user/subscription';

  public accessibleFeatureState: AccessibleFeatureState =
    AccessibleFeatureState.ALL;

  private regions$: Observable<JsonResponse<SubscriptionRegion[]>> | null =
    null;

  constructor(
    private http: HttpClient,
    private ga: GoogleAnalyticsService
  ) {}

  public checkPromoCode(
    promoCode: string
  ): Observable<JsonResponse<PromoCodeLookup>> {
    return this.http
      .get<
        JsonResponse<PromoCodeLookup>
      >(`${this.userSubscriptionURL}/promo?code=${promoCode}`)
      .pipe(
        catchError((err) => {
          console.error(
            `SubscriptionsService, PROMO_CODE_CHECK: ${promoCode}`,
            err
          );
          throw of(null);
        })
      );
  }

  /**
   * What a finished checkout actually charged, for the success page's
   * conversion value. Read back from Stripe rather than trusted from the
   * redirect URL: a promo code entered on the Stripe page, a proration or a
   * trial all make the price we opened the session with the wrong number,
   * and a value in the URL is a value the browser can edit.
   *
   * Null object when the session is not this user's or has no total.
   */
  public getCheckoutSessionAmount(
    sessionId: string
  ): Observable<JsonResponse<CheckoutSessionAmount | null>> {
    return this.http.get<JsonResponse<CheckoutSessionAmount | null>>(
      `${this.userSubscriptionURL}/session?sessionId=${encodeURIComponent(sessionId)}`
    );
  }

  public getRegions(): Observable<JsonResponse<SubscriptionRegion[]>> {
    if (!this.regions$) {
      this.regions$ = this.http
        .get<
          JsonResponse<SubscriptionRegion[]>
        >(`${this.userSubscriptionURL}/regions/all`)
        .pipe(
          shareReplay(1),
          catchError((err) => {
            console.error('SubscriptionsService, GET_REGIONS', err);
            this.regions$ = null;
            return of({ object: [], error: false } as any);
          })
        );
    }
    return this.regions$;
  }

  // Regions covering the location the user was interested in, used to
  // pre-fill the coverage area selection
  public getRecommendedRegions(
    lat: number,
    lon: number
  ): Observable<JsonResponse<SubscriptionRegion[]>> {
    return this.http
      .get<
        JsonResponse<SubscriptionRegion[]>
      >(`${this.userSubscriptionURL}/regions/recommended?lon=${lon}&lat=${lat}`)
      .pipe(
        catchError((err) => {
          console.error('SubscriptionsService, GET_RECOMMENDED_REGIONS', err);
          return of({ object: [], error: false } as any);
        })
      );
  }

  public handleSubscriptionPayment(
    planName: SubscriptionPlans,
    isPeriodMonthly: boolean,
    promoCode: string | null,
    regionType?: BackendRegionType,
    regionsIdsCommaSeparated?: string
  ): Observable<JsonResponse<SubscriptionPaymentUrl>> {
    let url = `${this.userSubscriptionURL}?plan=${planName}&monthly=${isPeriodMonthly}`;
    if (promoCode) {
      url += `&promoCode=${promoCode}`;
    }
    if (regionType) {
      url += `&regionType=${regionType}&regionsIdsCommaSeparated=${regionsIdsCommaSeparated ?? ''}`;
    }
    this.ga.logSubscriptionToPayment(planName, isPeriodMonthly, promoCode);
    return this.http.post<JsonResponse<SubscriptionPaymentUrl>>(url, null).pipe(
      catchError((err) => {
        // The cause travels with the message on purpose: this is the last
        // request before the money, and captureConsoleIntegration turns it
        // into the Sentry issue somebody reads when a purchase never
        // happened. Without `err` that issue says only which plan failed.
        console.error(
          `SubscriptionsService, SUBSCRIPTION_PAYMENT_REQUEST: ${planName}`,
          err
        );
        return throwError(() => err);
      })
    );
  }

  public updateSubscriptionAutoRenewal(
    isAutoRenewable: boolean
  ): Observable<JsonResponse<any>> {
    return this.http.post<JsonResponse<any>>(
      `${this.userSubscriptionURL}/${isAutoRenewable ? 'pause' : 'resume'}`,
      {}
    );
  }

  // public setAccessibleFeaturesId(): void {
  //   this.mapHttp.getAccessibleFeatures()
  //     .subscribe(data => {
  //       if (data.object.h3r5Cells && data.object.h3r7Cells) {
  //         this.accessibleFeaturesList = [...data.object.h3r5Cells, ...data.object.h3r7Cells];
  //         this.accessibleFeatureState = AccessibleFeatureState.LIMITED;
  //       } else {
  //         this.accessibleFeatureState = AccessibleFeatureState.ALL;
  //       }
  //     });
  // }

  // public getAccessibleFeatures(): { state: AccessibleFeatureState, features: NaturalId[] } {
  //   return {
  //     state: this.accessibleFeatureState,
  //     features: this.accessibleFeaturesList,
  //   };
  // }

  public setAccessibleFeaturesAll(): void {
    this.accessibleFeatureState = AccessibleFeatureState.ALL;
  }

  // public getIsAllFeaturesAvailable(): boolean {
  //   return this.getAccessibleFeatures().state === AccessibleFeatureState.ALL
  // }

  // public getIsFeatureAccessible(id: NaturalId): boolean {
  //   return this.getAccessibleFeatures().features.includes(id)
  // }
}
